import { useCallback, useEffect, useState } from "react";
import { Layout } from "@/components/layout";
import { Plus } from "lucide-react";
import { motion } from "framer-motion";
import { container } from "@/lib/utils";
import { EmptyVisits } from "@/components/ui/visits/empty-visits";
import { VisitList } from "@/components/ui/visits/visits-list";
import { useTranslation } from "react-i18next";
import { NewVisitSidebar } from "@/components/ui/visits/new-visit-sidebar";
import { useAuthStore } from "@/store/auth.store";
import { useVisitsStore } from "@/store/visits.store";
import { useLocation, useRouter } from "@tanstack/react-router";
import { SubscriptionOnlyButton } from "@/components/ui/SubscriptionOnlyButton/SubscriptionOnlyButton";
import { useRecordingUsageStore } from "@/store/recordingUsage.store";
import { ValidationSuscriptionButton } from "@/common/enums/enums";

export const Visits = (): JSX.Element => {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { getUser, user, userData } = useAuthStore();
  const { getFiles, files, loading } = useVisitsStore();
  const router = useRouter();
  const location = useLocation();
  const { fetchAndSetAvailableMinutes } = useRecordingUsageStore.getState();

  useEffect(() => {
    if (user?.email) {
      void getUser(user.email);
    }

    if (userData?.id) {
      void getFiles({ userId: userData.id, sparse: 1 });
      void fetchAndSetAvailableMinutes(userData.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retry = useCallback(
    (showLoading = true): void => {
      if (userData?.id) {
        void getFiles({ userId: userData.id, sparse: 1 }, showLoading);
      }
    },
    [userData?.id, getFiles]
  );

  useEffect(() => {
    if (!userData) return undefined;
    const intervalTask = setInterval(() => {
      void getFiles({ userId: userData.id, sparse: 1 }, false);
    }, 10000);
    return (): void => {
      clearInterval(intervalTask);
    };
  }, [userData, getFiles]);

  useEffect(() => {
    if (location.search.openNewVisit == true) {
      setSidebarOpen(true);
      router.history.replace(location.pathname, {});
    }
  }, [router.history, location.pathname, location.search]);

  return (
    <Layout
      isLoading={loading}
      navbarTitle={t("visits.title")}
      breadcrumbs={[
        { name: t("visits.title"), href: "/visits", current: true },
      ]}
    >
      <div className="min-h-screen p-6">
        <motion.div
          animate="show"
          className="mx-auto max-w-5xl space-y-8"
          initial="hidden"
          variants={container}
        >
          {files?.length === 0 ? (
            <EmptyVisits
              onOpenSidebar={() => {
                setSidebarOpen(true);
              }}
            />
          ) : (
            <div className="p-4">
              <div className="flex justify-end mb-6">
                <SubscriptionOnlyButton
                  validations={[ValidationSuscriptionButton.RECORDING_USAGES]}
                  onClick={() => {
                    setSidebarOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t("visits.newVisitButton")}
                </SubscriptionOnlyButton>
              </div>
              <VisitList
                visits={files}
                refresh={() => {
                  retry(false);
                }}
              />
            </div>
          )}
        </motion.div>
        <NewVisitSidebar open={sidebarOpen} onOpenChange={setSidebarOpen} />
      </div>
    </Layout>
  );
};
